import {
  createAnnouncement,
  createAnnouncementCard,
  deleteAnnouncementCard,
  getAnnouncementById,
  getAnnouncements,
  reorderAnnouncementCards,
  updateAnnouncementCard,
  updateAnnouncementStatus,
  updateAnnouncementTitle,
} from '@/api/announcements';
import {
  AnnouncementCard,
  AnnouncementStatus,
} from '@/api/announcements/types';
import { Constants } from '@/constants/announcements';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PreviewCard } from '../components/PreviewModal';
import { isRichTextEmpty } from '../components/RichTextEditor';
import {
  createMockAnnouncement,
  createMockAnnouncementCard,
  deleteMockAnnouncementCard,
  getActiveMockAnnouncement,
  getMockAnnouncement,
  reorderMockAnnouncementCards,
  updateMockAnnouncementCard,
  updateMockAnnouncementStatus,
  updateMockAnnouncementTitle,
  USE_MOCK,
} from '../mocks';
import { getYoutubeThumbnail } from '../videoUtils';

const MAX_CARDS = 5;

export interface EditableCard {
  key: number;
  id?: number;
  title: string;
  description: string;
  videoUrl: string;
  imageUrl: string | null;
  imageFile: File | null;
  collapsed: boolean;
}

interface UseAnnouncementFormHook {
  isEdit: boolean;
  isLoading: boolean;
  status: AnnouncementStatus;
  name: string;
  cards: EditableCard[];
  openPublish: boolean;
  openConvert: boolean;
  openPreview: boolean;
  previewCards: PreviewCard[];
  activeWarning: string | undefined;
  showErrors: boolean;
  setName: (name: string) => void;
  setOpenPublish: (open: boolean) => void;
  setOpenConvert: (open: boolean) => void;
  setOpenPreview: (open: boolean) => void;
  handleAddCard: () => void;
  handleRemoveCard: (key: number) => void;
  handleCardChange: (
    key: number,
    field: 'title' | 'description' | 'videoUrl',
    value: string
  ) => void;
  handleImageUpload: (key: number, file: File) => void;
  handleRemoveImage: (key: number) => void;
  handleMoveCard: (fromIndex: number, toIndex: number) => void;
  handleToggleCollapse: (key: number) => void;
  handleSaveDraft: () => Promise<void>;
  handleConfirmConvert: () => Promise<void>;
  handleOpenPublish: () => void;
  handleConfirmPublish: () => Promise<void>;
  handleBack: () => void;
}

let nextCardKey = 1;

function emptyCard(): EditableCard {
  nextCardKey += 1;
  return {
    key: nextCardKey,
    title: '',
    description: '',
    videoUrl: '',
    imageUrl: null,
    imageFile: null,
    collapsed: false,
  };
}

function isCardBlank(card: EditableCard): boolean {
  return !card.title.trim() && isRichTextEmpty(card.description);
}

function toEditableCard(card: AnnouncementCard): EditableCard {
  nextCardKey += 1;
  return {
    key: nextCardKey,
    id: card.id,
    title: card.rich_title,
    description: card.rich_text,
    videoUrl: card.video_url ?? '',
    imageUrl: card.image_url,
    imageFile: null,
    collapsed: false,
  };
}

export default function useAnnouncementForm(): UseAnnouncementFormHook {
  const [name, setName] = useState('');
  const [cards, setCards] = useState<EditableCard[]>([emptyCard()]);
  const [status, setStatus] = useState<AnnouncementStatus>('draft');
  const [openPublish, setOpenPublish] = useState(false);
  const [openConvert, setOpenConvert] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const { id } = useParams();
  const announcementId = Number(id);
  const isEdit = Boolean(id);

  const [isLoading, setIsLoading] = useState(isEdit);

  const originalCardsRef = useRef<AnnouncementCard[]>([]);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();
  const navigate = useNavigate();

  const loadAnnouncement = useCallback(async () => {
    if (!isEdit) return;
    setIsLoading(true);
    try {
      if (USE_MOCK) {
        const mock = getMockAnnouncement(announcementId);
        if (!mock) throw new Error(Constants.notFound);
        setName(mock.title);
        setStatus(mock.status);
        originalCardsRef.current = mock.cards;
        setCards(
          [...mock.cards].sort((a, b) => a.order - b.order).map(toEditableCard)
        );
        return;
      }

      const response = await getAnnouncementById(announcementId);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (!response.data) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setName(response.data.title);
      setStatus(response.data.status);
      originalCardsRef.current = response.data.cards;
      setCards(
        [...response.data.cards]
          .sort((a, b) => a.order - b.order)
          .map(toEditableCard)
      );
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
      navigate('/announcements');
    } finally {
      setIsLoading(false);
    }
  }, [isEdit, announcementId, handleSnackbar, getErrorMessage, navigate]);

  useEffect(() => {
    loadAnnouncement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, announcementId]);

  const [activeAnnouncement, setActiveAnnouncement] = useState<{
    id: number;
    title: string;
  }>();

  useEffect(() => {
    if (USE_MOCK) return;
    getAnnouncements('active', 1, 1)
      .then((response) => {
        const active = response.data?.[0];
        if (active)
          setActiveAnnouncement({ id: active.id, title: active.title });
      })
      .catch(() => undefined);
  }, []);

  const currentActive = USE_MOCK
    ? getActiveMockAnnouncement()
    : activeAnnouncement;
  const activeWarning =
    currentActive && (!isEdit || currentActive.id !== announcementId)
      ? Constants.activeWillBeDeactivated(currentActive.title)
      : undefined;

  const handleAddCard = (): void => {
    if (cards.length >= MAX_CARDS) {
      handleSnackbar(Constants.maxCards, true);
      return;
    }
    setCards((prev) => [...prev, emptyCard()]);
  };

  const handleRemoveCard = (key: number): void => {
    setCards((prev) =>
      prev.length > 1 ? prev.filter((card) => card.key !== key) : prev
    );
  };

  const handleCardChange = (
    key: number,
    field: 'title' | 'description' | 'videoUrl',
    value: string
  ): void => {
    setCards((prev) =>
      prev.map((card) =>
        card.key === key ? { ...card, [field]: value } : card
      )
    );
  };

  const handleImageUpload = (key: number, file: File): void => {
    if (file.type === 'image/gif') {
      handleSnackbar(Constants.gifNotAllowed, true);
      return;
    }

    const imageUrl = URL.createObjectURL(file);
    setCards((prev) =>
      prev.map((card) =>
        card.key === key
          ? { ...card, imageUrl, imageFile: file, videoUrl: '' }
          : card
      )
    );
  };

  const handleRemoveImage = (key: number): void => {
    setCards((prev) =>
      prev.map((card) =>
        card.key === key ? { ...card, imageUrl: null, imageFile: null } : card
      )
    );
  };

  const handleToggleCollapse = (key: number): void => {
    setCards((prev) =>
      prev.map((card) =>
        card.key === key ? { ...card, collapsed: !card.collapsed } : card
      )
    );
  };

  const handleMoveCard = (fromIndex: number, toIndex: number): void => {
    if (fromIndex === toIndex) return;
    setCards((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  };

  const validateName = (): boolean => {
    if (!name.trim()) {
      setShowErrors(true);
      handleSnackbar(Constants.requiredName, true);
      return false;
    }

    setShowErrors(false);
    return true;
  };

  const validateVideoUrls = (): boolean => {
    const hasInvalidVideo = cards.some(
      (card) => card.videoUrl.trim() && !getYoutubeThumbnail(card.videoUrl)
    );

    if (hasInvalidVideo) {
      setShowErrors(true);
      setCards((prev) =>
        prev.map((card) =>
          card.videoUrl.trim() && !getYoutubeThumbnail(card.videoUrl)
            ? { ...card, collapsed: false }
            : card
        )
      );
      handleSnackbar(Constants.invalidVideoUrl, true);
      return false;
    }

    return true;
  };

  const validate = (): boolean => {
    if (!validateName()) return false;

    const hasInvalidCard = cards.some(
      (card) => !card.title.trim() || isRichTextEmpty(card.description)
    );

    if (hasInvalidCard) {
      setShowErrors(true);
      setCards((prev) =>
        prev.map((card) =>
          !card.title.trim() || isRichTextEmpty(card.description)
            ? { ...card, collapsed: false }
            : card
        )
      );
      handleSnackbar(Constants.requiredCardFields, true);
      return false;
    }

    return validateVideoUrls();
  };

  const saveCardsMock = (targetId: number): void => {
    const keptIds = new Set(
      cards.filter((c) => c.id).map((c) => c.id as number)
    );
    const blankExistingIds = cards
      .filter((card) => card.id && isCardBlank(card))
      .map((card) => card.id as number);

    originalCardsRef.current
      .filter((card) => !keptIds.has(card.id))
      .forEach((card) => deleteMockAnnouncementCard(card.id));
    blankExistingIds.forEach((cardId) => deleteMockAnnouncementCard(cardId));

    const finalIds: number[] = [];

    cards
      .filter((card) => !isCardBlank(card))
      .forEach((card) => {
        const richTitle = card.title.trim();
        const richText = card.description.trim();

        if (!card.id) {
          const created = createMockAnnouncementCard(targetId, {
            richTitle,
            richText,
            videoUrl: card.imageFile ? null : card.videoUrl.trim() || null,
            image: card.imageFile,
          });
          finalIds.push(created.id);
          return;
        }

        const original = originalCardsRef.current.find((c) => c.id === card.id);
        const removeImage =
          !card.imageFile && !card.imageUrl && Boolean(original?.image_url);

        updateMockAnnouncementCard(card.id, {
          richTitle,
          richText,
          videoUrl: card.imageFile ? undefined : card.videoUrl.trim(),
          image: card.imageFile,
          removeImage,
        });
        finalIds.push(card.id);
      });

    if (finalIds.length > 0) {
      reorderMockAnnouncementCards(targetId, finalIds);
    }
  };

  const saveCards = async (targetId: number): Promise<void> => {
    if (USE_MOCK) {
      saveCardsMock(targetId);
      return;
    }

    const keptIds = new Set(
      cards.filter((c) => c.id).map((c) => c.id as number)
    );
    const blankExistingIds = cards
      .filter((card) => card.id && isCardBlank(card))
      .map((card) => card.id as number);
    const deletedIds = [
      ...originalCardsRef.current
        .filter((card) => !keptIds.has(card.id))
        .map((card) => card.id),
      ...blankExistingIds,
    ];

    await Promise.all(
      deletedIds.map((cardId) => deleteAnnouncementCard(cardId))
    );

    const cardsToSave = cards.filter((card) => !isCardBlank(card));
    const finalIds: number[] = [];

    for (let index = 0; index < cardsToSave.length; index += 1) {
      const card = cardsToSave[index];
      const richTitle = card.title.trim();
      const richText = card.description.trim();

      if (!card.id) {
        // eslint-disable-next-line no-await-in-loop
        const response = await createAnnouncementCard(targetId, {
          richTitle,
          richText,
          videoUrl: card.imageFile ? null : card.videoUrl.trim() || null,
          image: card.imageFile,
        });
        if (response.detail.description) {
          throw new Error(response.detail.description);
        }
        if (!response.data) {
          throw new Error('Algo deu errado, tente novamente.');
        }
        finalIds.push(response.data.id);
      } else {
        const original = originalCardsRef.current.find((c) => c.id === card.id);
        const removeImage =
          !card.imageFile && !card.imageUrl && Boolean(original?.image_url);

        // eslint-disable-next-line no-await-in-loop
        const response = await updateAnnouncementCard(card.id, {
          richTitle,
          richText,
          videoUrl: card.imageFile ? undefined : card.videoUrl.trim(),
          image: card.imageFile,
          removeImage,
        });
        if (response.detail.description) {
          throw new Error(response.detail.description);
        }
        finalIds.push(card.id);
      }
    }

    if (finalIds.length > 0) {
      const orderResponse = await reorderAnnouncementCards(targetId, finalIds);
      if (orderResponse.detail.description) {
        throw new Error(orderResponse.detail.description);
      }
    }
  };

  const saveAnnouncement = async (): Promise<number> => {
    if (USE_MOCK) {
      if (isEdit) {
        updateMockAnnouncementTitle(announcementId, name.trim());
        saveCardsMock(announcementId);
        return announcementId;
      }
      const created = createMockAnnouncement(name.trim());
      saveCardsMock(created.id);
      return created.id;
    }

    if (isEdit) {
      const response = await updateAnnouncementTitle(
        announcementId,
        name.trim()
      );
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      await saveCards(announcementId);
      return announcementId;
    }

    const response = await createAnnouncement(name.trim());
    if (response.detail.description) {
      throw new Error(response.detail.description);
    }
    if (!response.data) {
      throw new Error('Algo deu errado, tente novamente.');
    }
    await saveCards(response.data.id);
    return response.data.id;
  };

  const handleSaveDraft = async (): Promise<void> => {
    if (!validateName()) return;
    if (!validateVideoUrls()) return;

    if (isEdit && status !== 'draft') {
      setOpenConvert(true);
      return;
    }

    try {
      await saveAnnouncement();
      handleSnackbar(Constants.savedAsDraft, false);
      navigate('/announcements');
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  const handleConfirmConvert = async (): Promise<void> => {
    setOpenConvert(false);
    try {
      const savedId = await saveAnnouncement();
      if (USE_MOCK) {
        updateMockAnnouncementStatus(savedId, 'draft');
      } else {
        const response = await updateAnnouncementStatus(savedId, 'draft');
        if (response.detail.description) {
          throw new Error(response.detail.description);
        }
      }
      handleSnackbar(Constants.convertedToDraft, false);
      navigate('/announcements');
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  const handleOpenPublish = (): void => {
    if (!validate()) return;
    setOpenPublish(true);
  };

  const handleConfirmPublish = async (): Promise<void> => {
    setOpenPublish(false);
    try {
      const savedId = await saveAnnouncement();
      if (status !== 'active') {
        if (USE_MOCK) {
          updateMockAnnouncementStatus(savedId, 'active');
        } else {
          const response = await updateAnnouncementStatus(savedId, 'active');
          if (response.detail.description) {
            throw new Error(response.detail.description);
          }
        }
      }
      handleSnackbar(Constants.published, false);
      navigate('/announcements');
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  const handleBack = (): void => {
    navigate(-1);
  };

  const previewCards: PreviewCard[] = cards.map((card) => ({
    title: card.title,
    description: card.description,
    videoUrl: card.videoUrl.trim() || null,
    imageUrl: card.imageUrl,
  }));

  return {
    isEdit,
    isLoading,
    status,
    name,
    cards,
    openPublish,
    openConvert,
    openPreview,
    previewCards,
    activeWarning,
    showErrors,
    setName,
    setOpenPublish,
    setOpenConvert,
    setOpenPreview,
    handleAddCard,
    handleRemoveCard,
    handleCardChange,
    handleImageUpload,
    handleRemoveImage,
    handleMoveCard,
    handleToggleCollapse,
    handleSaveDraft,
    handleConfirmConvert,
    handleOpenPublish,
    handleConfirmPublish,
    handleBack,
  };
}
